import React, { useState, useEffect } from "react";
import ComponentWrapper from "../components/Shared/ComponentWrapper";

const WP_SECTIONS = [
  { id: "abstract", label: "Abstract" },
  { id: "introduction", label: "1. Introduction" },
  { id: "problem", label: "2. Problem Statement" },
  { id: "solution", label: "3. The Helix AI Solution" },
  { id: "architecture", label: "4. Architecture" },
  { id: "ai-agent", label: "5. AI Agent Framework" },
  { id: "blockchain-ops", label: "6. Blockchain Operations" },
  { id: "security", label: "7. Security Model" },
  { id: "tokenomics", label: "8. Tokenomics" },
  { id: "token-metering", label: "9. Helix Token Metering" },
  { id: "extensibility", label: "10. Access & Extensibility" },
  { id: "governance", label: "11. Governance" },
  { id: "roadmap", label: "12. Roadmap" },
  { id: "risks", label: "13. Risk Factors" },
  { id: "conclusion", label: "14. Conclusion" },
];

const WhitepaperPage = () => {
  const [activeSection, setActiveSection] = useState("abstract");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = WP_SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        if (!el) return { id, top: Infinity };
        return { id, top: el.getBoundingClientRect().top };
      });
      const current = offsets.reduce(
        (closest, item) =>
          item.top >= -100 && item.top < closest.top ? item : closest,
        { id: offsets[0].id, top: Infinity }
      );
      setActiveSection(current.id);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="w-full min-h-screen bg-white">
      <ComponentWrapper styles="pt-12 sm:pt-20 pb-20">
        {/* Header */}
        <div className="mb-12">
          <p className="text-[14px] text-blue-2 font-semibold mb-2 tracking-wide uppercase">
            Whitepaper
          </p>
          <h1 className="text-[36px] sm:text-[48px] font-semibold text-blue-1 leading-tight mb-4">
            Helix AI: Autonomous Blockchain Intelligence
          </h1>
          <p className="text-[18px] text-gray-1 max-w-[680px] leading-[28px] mb-2">
            A comprehensive technical and economic overview of the Helix AI
            platform — an autonomous AI agent for interacting with Ethereum
            through natural language.
          </p>
          <p className="text-[14px] text-gray-1">Version 1.1 — July 2026</p>
        </div>

        <div className="flex gap-12 relative">
          {/* Sidebar */}
          <aside className="hidden lg:block w-[240px] flex-shrink-0">
            <nav className="sticky top-[100px]">
              <ul className="flex flex-col gap-0.5">
                {WP_SECTIONS.map(({ id, label }) => (
                  <li key={id}>
                    <button
                      onClick={() => scrollTo(id)}
                      className={`text-left w-full text-[13px] px-3 py-1.5 rounded-[8px] transition-all duration-200 ${
                        activeSection === id
                          ? "bg-blue-2/10 text-blue-2 font-semibold"
                          : "text-gray-1 hover:text-blue-1 hover:bg-gray-50"
                      }`}
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            {/* Abstract */}
            <Section id="abstract" title="Abstract">
              <P>
                Helix AI is an autonomous Ethereum AI agent that enables users
                to interact with the Ethereum network through natural language.
                By combining large language models with a comprehensive suite of
                blockchain tools, Helix AI abstracts away the complexity of
                decentralized finance (DeFi), smart contract development, and
                on-chain asset management. Users connect a wallet, describe
                their intent in plain language, and the AI agent interprets,
                plans, and executes the corresponding on-chain operations —
                including token swaps, smart contract deployments, transfers,
                and portfolio management on Ethereum.
              </P>
              <P>
                The platform is powered by HLX, a utility token on the Ethereum
                blockchain that fuels the usage-metered Helix Token balance
                underpinning all agent interactions. This paper presents the
                technical architecture,
                security model, economic design, and long-term vision for the
                Helix AI ecosystem.
              </P>
            </Section>

            <Divider />

            {/* 1. Introduction */}
            <Section id="introduction" title="1. Introduction">
              <P>
                The blockchain ecosystem has grown from a niche technology
                experiment to a global financial infrastructure supporting
                trillions of dollars in value. Yet the user experience remains a
                significant barrier to mainstream adoption. Interacting with
                decentralized protocols requires navigating fragmented
                interfaces, understanding gas mechanics, managing approvals,
                interpreting transaction parameters, and coordinating multiple
                protocols on Ethereum.
              </P>
              <P>
                At the same time, advances in large language models (LLMs) have
                demonstrated that AI can serve as a powerful intermediary between
                human intent and complex system operations. LLMs can parse
                natural language, reason about multi-step processes, and
                orchestrate tool calls to achieve user-specified goals.
              </P>
              <P>
                Helix AI exists at the intersection of these two trends. It is a
                full-stack conversational AI agent that transforms natural
                language instructions into precise blockchain operations. Rather
                than forcing users to learn the intricacies of each protocol,
                chain, or smart contract interface, Helix AI allows them to
                simply state what they want to do and have the agent handle the
                rest — from route optimization to transaction construction to
                on-chain execution.
              </P>
              <H3>1.1 Vision</H3>
              <P>
                Our vision is to make blockchain interaction as simple as having
                a conversation. Every person, regardless of technical
                background, should be able to deploy smart contracts, manage
                their portfolio, and participate in DeFi on Ethereum — by
                describing what they want in their own words.
              </P>
              <H3>1.2 Mission</H3>
              <P>
                To build the most capable and trustworthy autonomous Ethereum
                agent — one that combines the intelligence of frontier AI models
                with the security of non-custodial wallet architecture on the
                most battle-tested smart contract network.
              </P>
            </Section>

            <Divider />

            {/* 2. Problem Statement */}
            <Section id="problem" title="2. Problem Statement">
              <H3>2.1 Fragmented User Experience</H3>
              <P>
                Even within Ethereum, the landscape is fragmented across
                hundreds of protocols and thousands of tokens. Users
                must navigate separate interfaces for swapping tokens,
                deploying contracts, checking balances, and
                managing governance positions. Each interface has its own
                learning curve, terminology, and potential for user error.
              </P>
              <H3>2.2 Technical Barriers</H3>
              <P>
                Deploying a smart contract requires knowledge of Solidity,
                compilation toolchains, deployment scripts, and block explorer
                verification. Executing a token swap requires understanding
                slippage, approval flows, and liquidity routing across the many
                decentralized exchanges on Ethereum.
              </P>
              <H3>2.3 Security Risks from Complexity</H3>
              <P>
                The complexity of blockchain interactions creates security risks.
                Users may inadvertently approve malicious contracts, send tokens
                to wrong addresses, or fall victim to phishing attacks when
                navigating unfamiliar interfaces. Each additional step in a
                workflow is an opportunity for error.
              </P>
              <H3>2.4 Portfolio Fragmentation</H3>
              <P>
                Managing assets on Ethereum requires manually tracking holdings,
                approvals, and positions scattered across many protocols. There
                is no unified, conversational interface for viewing or managing a
                complete on-chain portfolio.
              </P>
            </Section>

            <Divider />

            {/* 3. The Helix AI Solution */}
            <Section id="solution" title="3. The Helix AI Solution">
              <P>
                Helix AI addresses these challenges by providing a single
                conversational interface through which users can perform any
                blockchain operation on Ethereum. The core design
                principles are:
              </P>
              <H3>3.1 Natural Language as the Universal Interface</H3>
              <P>
                Instead of learning a different UI for every protocol, users
                describe their intent in plain English. The AI agent parses the
                request, determines the optimal execution strategy, and presents
                a clear summary for user confirmation before executing. Examples:
              </P>
              <Ul>
                <Li>"Swap 1 ETH for USDC"</Li>
                <Li>"Deploy an ERC-20 token called HelixGold with 10M supply"</Li>
                <Li>"Send 250 USDC to 0xabc..."</Li>
                <Li>"What's my total portfolio value?"</Li>
              </Ul>

              <H3>3.2 Non-Custodial by Design</H3>
              <P>
                Users connect their own wallets and retain full custody of their
                private keys and assets at all times. Helix AI uses session-based
                signing to propose transactions, but every transaction requires
                explicit user confirmation. The agent never has direct access to
                private keys.
              </P>

              <H3>3.3 Ethereum-Native</H3>
              <P>
                Helix AI is built natively on Ethereum mainnet — the most
                secure and liquid smart contract network. Users interact with a
                single agent that handles protocol selection, gas management,
                and transaction construction on Ethereum transparently.
              </P>

              <H3>3.4 Comprehensive Tool Suite</H3>
              <P>
                The agent is equipped with a suite of specialized blockchain
                tools covering token swaps (via DEX aggregation),
                smart contract compilation and deployment, portfolio tracking,
                price lookups, and transfers. Each tool is purpose-built for its
                domain and rigorously tested.
              </P>
            </Section>

            <Divider />

            {/* 4. Architecture */}
            <Section id="architecture" title="4. Architecture">
              <P>
                Helix AI is deployed as a serverless Next.js application on
                Vercel, backed by a Neon PostgreSQL database. The architecture
                is designed for low latency, high availability, and horizontal
                scalability.
              </P>

              <H3>4.1 Application Layer</H3>
              <P>
                The frontend is a responsive web application optimized for both
                desktop and mobile. It provides a real-time chat interface with
                streaming AI responses rendered in markdown. The application
                connects to user wallets via Thirdweb's in-app wallet
                infrastructure, supporting multiple authentication methods
                (Google, Apple, email, phone, passkey, and external wallets).
              </P>

              <H3>4.2 AI Processing Layer</H3>
              <P>
                The core AI engine is built on the Vercel AI SDK, which provides
                streaming response generation, tool orchestration, and
                multi-step execution. The agent uses a frontier language model
                to interpret user intent, select appropriate tools, construct
                tool call parameters, and synthesize results into human-readable
                responses.
              </P>
              <P>
                The agent operates with a system prompt that defines its
                personality, capabilities, and behavioral constraints. It is
                instructed to always ask for confirmation before executing
                transactions, explain costs and risks, and provide educational
                context when helpful.
              </P>

              <H3>4.3 Tool Execution Layer</H3>
              <P>
                Each blockchain operation is implemented as a discrete tool with
                a defined schema, input validation, and error handling. Tools
                are categorized as:
              </P>
              <Ul>
                <Li>
                  <strong>Stateless tools:</strong> Read-only operations that do
                  not modify state (balance queries, price lookups, token
                  searches, chain listings).
                </Li>
                <Li>
                  <strong>Stateful tools:</strong> Write operations that modify
                  on-chain or off-chain state (contract deployments, token
                  swaps, transfers). These tools interact with the
                  database to track Helix Token usage and transaction history.
                </Li>
                <Li>
                  <strong>RAG tools:</strong> Retrieval-augmented generation
                  tools that query an indexed knowledge base (built with
                  LlamaIndex) containing documentation about the Helix protocol,
                  HLX token, and blockchain concepts.
                </Li>
              </Ul>

              <H3>4.4 Wallet Infrastructure</H3>
              <P>
                Wallet management is handled through Thirdweb's smart account
                infrastructure, which provides account abstraction with gas
                sponsorship on Ethereum. Session keys are created to allow the
                agent to propose transactions on the user's behalf without
                requiring the user's private key. Session keys are scoped to
                Ethereum and have configurable durations.
              </P>

              <H3>4.5 Data Layer</H3>
              <P>
                A Neon PostgreSQL database stores user records, Helix Token
                balances, usage events, chat history, transaction logs, and
                deployed contract metadata.
                The database is accessed through Drizzle ORM with type-safe
                schema definitions. Sensitive data is encrypted at rest.
              </P>
            </Section>

            <Divider />

            {/* 5. AI Agent Framework */}
            <Section id="ai-agent" title="5. AI Agent Framework">
              <H3>5.1 Intent Recognition</H3>
              <P>
                The AI agent uses a frontier language model to parse user
                messages and determine intent. It can handle ambiguous or
                incomplete requests by asking clarifying questions. For example,
                if a user says "swap some ETH", the agent will ask how much ETH
                and which token they'd like to receive.
              </P>

              <H3>5.2 Multi-Step Execution</H3>
              <P>
                Complex operations may require multiple sequential tool calls.
                The agent can chain up to 10 tool calls per turn, allowing it to
                handle workflows such as: check balance → get swap quote →
                confirm with user → execute swap → report result. Each step is
                logged and visible to the user.
              </P>

              <H3>5.3 Context and Memory</H3>
              <P>
                The agent maintains conversation context within a session,
                allowing users to refer back to previous messages, build on
                earlier requests, and have natural multi-turn conversations.
                Chat history is persisted in the database for continuity across
                sessions.
              </P>

              <H3>5.4 Knowledge Base (RAG)</H3>
              <P>
                The agent is augmented with a retrieval-augmented generation
                (RAG) system built on LlamaIndex. The knowledge base contains
                curated information about the Helix protocol, HLX token
                mechanics, DeFi concepts, and blockchain fundamentals. When
                users ask informational questions, the agent retrieves relevant
                context from the knowledge base to provide accurate, up-to-date
                answers.
              </P>

              <H3>5.5 Safety Guardrails</H3>
              <P>
                The agent is designed with multiple safety mechanisms:
              </P>
              <Ul>
                <Li>
                  Mandatory confirmation before any state-changing transaction.
                </Li>
                <Li>
                  Balance checks before initiating transfers or swaps to prevent
                  insufficient-fund errors.
                </Li>
                <Li>
                  Explicit cost disclosure for token-consuming actions
                  (especially contract deployments).
                </Li>
                <Li>
                  Rate limiting and step count caps (maximum 10 tool calls per
                  turn) to prevent runaway execution.
                </Li>
                <Li>
                  Input validation and sanitization on all tool parameters.
                </Li>
              </Ul>
            </Section>

            <Divider />

            {/* 6. Blockchain Operations */}
            <Section id="blockchain-ops" title="6. Blockchain Operations">
              <H3>6.1 Token Swaps</H3>
              <P>
                Swaps are powered by the WowMax DEX aggregator, which routes
                trades across multiple decentralized exchanges to find optimal
                pricing. The flow is: quote retrieval → user confirmation →
                transaction construction → execution. All swaps include a
                default 1% slippage tolerance to protect against price movement
                during execution.
              </P>

              <H3>6.2 Smart Contract Deployment</H3>
              <P>
                The platform supports two deployment modes:
              </P>
              <Ul>
                <Li>
                  <strong>ERC-20 tokens (10,000 HT):</strong> Uses a
                  battle-tested template. Users specify name, symbol, decimals,
                  and total supply. The contract is compiled, deployed, and
                  verified automatically.
                </Li>
                <Li>
                  <strong>Custom Solidity contracts (100,000 HT):</strong>{" "}
                  Users can describe or paste arbitrary Solidity code. The
                  agent compiles using solc with the optimizer
                  enabled (200 runs), deploys from the user's wallet, and
                  attempts block explorer verification.
                </Li>
              </Ul>
              <P>
                OpenZeppelin imports (e.g., @openzeppelin/contracts/...) are
                automatically resolved via CDN, enabling access to the full
                OpenZeppelin library without local installation.
              </P>

              <H3>6.3 Portfolio Management</H3>
              <P>
                Users can query their portfolio on Ethereum. The
                agent retrieves native token balances, ERC-20 token holdings,
                and NFT collections for any wallet address. Real-time price data
                from CoinGecko enables USD valuations.
              </P>

              <H3>6.4 Token Transfers</H3>
              <P>
                The agent supports both native token transfers and ERC-20 token
                transfers to any address. Before executing, the agent verifies
                sufficient balance and presents the transaction details for
                confirmation.
              </P>
            </Section>

            <Divider />

            {/* 7. Security Model */}
            <Section id="security" title="7. Security Model">
              <H3>7.1 Non-Custodial Architecture</H3>
              <P>
                Helix AI is fundamentally non-custodial. The platform never
                stores, accesses, or controls user private keys. All transaction
                signing occurs on the client side or through Thirdweb's smart
                account infrastructure, where session keys are scoped and
                time-limited.
              </P>

              <H3>7.2 Session Key Model</H3>
              <P>
                When a user connects, a session key is created that allows the
                agent to propose transactions. Session keys are:
              </P>
              <Ul>
                <Li>Scoped to the Ethereum network.</Li>
                <Li>
                  Limited in permissions — they can propose but cannot
                  unilaterally execute without user confirmation.
                </Li>
                <Li>Revocable by the user at any time.</Li>
              </Ul>

              <H3>7.3 Gas Sponsorship</H3>
              <P>
                On Ethereum, transactions are gas-sponsored through
                account abstraction, removing the need for users to hold ETH
                for gas fees. This lowers the barrier to entry for new users.
              </P>

              <H3>7.4 Transaction Confirmation</H3>
              <P>
                Every state-changing operation requires explicit user
                confirmation. The agent presents transaction details — including
                amounts, addresses, estimated fees, and Helix Token costs —
                before execution. Users can reject any transaction at any point.
              </P>

              <H3>7.5 Data Security</H3>
              <P>
                All data in transit is encrypted via TLS. Data at rest in the
                PostgreSQL database is encrypted. Access to production systems
                is restricted and audited. API endpoints are authenticated and
                rate-limited.
              </P>
            </Section>

            <Divider />

            {/* 8. Tokenomics */}
            <Section id="tokenomics" title="8. Tokenomics">
              <H3>8.1 HLX Token Overview</H3>
              <P>
                HLX is an ERC-20 utility token deployed on the Ethereum
                blockchain. It serves as the primary medium of exchange within
                the Helix AI ecosystem.
              </P>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <StatCard label="Token Name">Helix (HLX)</StatCard>
                <StatCard label="Network">Ethereum (Chain ID 1)</StatCard>
                <StatCard label="Standard">ERC-20</StatCard>
                <StatCard label="Decimals">18</StatCard>
                <StatCard label="Total Supply">100,000,000,000 HLX</StatCard>
                <StatCard label="Contract">
                  <code className="text-[12px] break-all">
                    0x28d4e499c4cde621e1cea7c9cbf9d43bf75a9525
                  </code>
                </StatCard>
              </div>

              <H3>8.2 Utility</H3>
              <P>HLX serves the following functions within the ecosystem:</P>
              <Ul>
                <Li>
                  <strong>Helix Token top-ups:</strong> Users spend HLX to
                  acquire Helix Tokens (HT) — the usage-metered balance that
                  powers all AI agent interactions. 1 HLX = 1,000 HT.
                </Li>
                <Li>
                  <strong>Governance participation:</strong> HLX holders can
                  participate in governance decisions regarding platform
                  development, new protocol integrations, and feature prioritization.
                </Li>
                <Li>
                  <strong>Ecosystem alignment:</strong> HLX creates economic
                  alignment between users and the platform, incentivizing active
                  participation and long-term engagement.
                </Li>
              </Ul>

              <H3>8.3 Supply Distribution</H3>
              <P>
                The total supply of 100 billion HLX is allocated across the
                following categories, ensuring a fair launch and long-term
                ecosystem sustainability:
              </P>
              <Ul>
                <Li>
                  <strong>Decentralized Token Sale (50%):</strong> Sold to the
                  public through a fully decentralized, on-chain token sale —
                  putting the majority of the supply directly into the hands of
                  the community with no private allocations. Of this allocation,
                  35% is subject to a vesting schedule with a 1-year cliff —
                  during which no tokens unlock — followed by a 6-year linear
                  release beginning June 1, 2027 at 500M HLX per month. Together,
                  the cliff and linear vesting span a total 7-year distribution
                  horizon that supports sustainable, long-term alignment.
                </Li>
                <Li>
                  <strong>Treasury (40%):</strong> Held in the Treasury wallet to
                  fund ecosystem rewards, staking rewards, grants, and developers
                  building on the Helix AI platform. All treasury disbursements
                  are governed on-chain through the Timelock and governance
                  process.
                </Li>
                <Li>
                  <strong>Onchain Liquidity (10%):</strong> Deployed into onchain
                  liquidity such as automated market makers and lending protocols
                  to ensure deep, healthy markets and accessible trading for HLX.
                </Li>
              </Ul>

              <H3>8.4 Treasury Release Schedule</H3>
              <P>
                To ensure predictable, sustainable funding for the ecosystem, the
                Treasury contract follows a programmatic release schedule. Since
                inception, the Treasury releases a{" "}
                <strong>100M HLX monthly allocation</strong> that{" "}
                <strong>compounds month over month</strong> — each month's
                releasable allocation grows relative to the prior month, steadily
                increasing the funding available for staking rewards, ecosystem
                incentives, grants, and developer support as the platform scales.
              </P>
              <P>
                This release rate is not fixed in perpetuity. Like all Treasury
                parameters, the monthly allocation and its compounding rate can
                be adjusted at any time through on-chain governance, executed via
                the Timelock. This gives HLX stakers direct control over the pace
                of ecosystem emissions, balancing growth incentives against
                long-term supply discipline.
              </P>
            </Section>

            <Divider />

            {/* 9. Helix Token Metering */}
            <Section id="token-metering" title="9. Helix Token Metering">
              <H3>9.1 Usage-Based Economics</H3>
              <P>
                Platform usage is metered in <strong>Helix Tokens (HT)</strong>,
                a prepaid balance that users top up with HLX. Rather than a flat
                fee per message, each interaction burns HT in proportion to the{" "}
                <strong>real AI compute it consumes</strong>. This keeps simple
                questions extremely cheap while fairly pricing long, complex
                agentic tasks. Balances are tracked internally in micro-HT
                integers to avoid floating-point drift; HT is the display unit.
              </P>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                <StatCard label="Peg">1 HLX = 1,000 HT</StatCard>
                <StatCard label="Price per Helix Token">$0.00004</StatCard>
                <StatCard label="Free daily allowance">2,500 HT / day</StatCard>
                <StatCard label="Max per purchase">10,000,000 HT</StatCard>
              </div>

              <H3>9.2 Per-Message Metering</H3>
              <P>
                Chat is billed on actual token usage. When a message completes,
                the platform reads the model's real input and output token counts,
                multiplies by that model's live per-token price, applies a fixed
                margin (1.5×), and converts the result into HT. Billing is
                post-usage: a balance check gates each request, and the exact
                charge is recorded per message. Pricier models and longer replies
                burn proportionally more HT.
              </P>

              <H3>9.3 Service Fees</H3>
              <P>
                On-chain deployments carry a flat HT fee on top of gas, reflecting
                the cost of Solidity compilation, deployment construction, and
                block explorer verification:
              </P>
              <div className="rounded-[16px] border border-[#F0F0F5] overflow-hidden my-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#FBFAFA]">
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3">
                        Action
                      </th>
                      <th className="text-[14px] font-semibold text-blue-1 px-5 py-3 text-right">
                        Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Chat message", "Metered (model cost × 1.5)"],
                      ["Swaps, transfers, portfolio, price lookups", "Metered (per message)"],
                      ["ERC-20 token deployment", "10,000 HT"],
                      ["Custom contract deployment", "100,000 HT"],
                    ].map(([action, cost], i) => (
                      <tr key={i} className="border-t border-[#F0F0F5]">
                        <td className="text-[14px] text-gray-700 px-5 py-3">
                          {action}
                        </td>
                        <td className="text-[14px] text-blue-1 font-medium px-5 py-3 text-right">
                          {cost}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <H3>9.4 Free Daily Allowance</H3>
              <P>
                Every user receives 2,500 free Helix Tokens per day, refreshed
                daily. Free tokens are non-transferable, do not roll over, and are
                always spent before any purchased balance — so every user can use
                the platform each day without a financial commitment.
              </P>

              <H3>9.5 Acquiring Helix Tokens</H3>
              <P>
                Additional Helix Tokens are purchased by sending HLX from the
                user's wallet to the platform's designated wallet at a fixed rate
                of 1 HLX = 1,000 HT, up to 10,000,000 HT per transaction. Tokens
                are credited instantly upon confirmation of the HLX transfer.
              </P>
            </Section>

            <Divider />

            {/* 10. Access & Extensibility */}
            <Section id="extensibility" title="10. Access & Extensibility">
              <H3>10.1 Multi-Model Access</H3>
              <P>
                Helix is model-agnostic. Through a unified model router, users can
                choose from 300+ AI models spanning providers such as OpenAI
                (including GPT-6 Astra and GPT-6 Astra Pro), Anthropic, Google,
                and Meta — all behind a single Helix Token balance and interface.
                Because the agent depends on tool calling to perform on-chain
                actions, the selector only surfaces tool-capable models,
                guaranteeing every selectable model works with the full agent.
                Model choice is persisted per user, and cost scales with each
                model's per-token price.
              </P>

              <H3>10.2 Skills</H3>
              <P>
                Skills let users extend the agent with their own instructions and
                domain knowledge. Each skill is a named, markdown document
                describing a procedure, default, or knowledge set. Enabled skills
                are advertised to the agent by name and description; when a request
                matches, the agent loads the skill's full instructions before
                acting and follows them. This lets users encode repeatable
                workflows and preferences without re-explaining context each time.
              </P>

              <H3>10.3 Developer API</H3>
              <P>
                Helix exposes an OpenAI-compatible API so developers can invoke the
                agent from their own applications, scripts, and backends. Any
                OpenAI SDK works by pointing its base URL at{" "}
                <code className="text-[13px] text-blue-2">
                  https://ai.helix.org/api/v1
                </code>
                . There are two ways to pay:
              </P>
              <Ul>
                <Li>
                  <strong>API keys</strong> (prefixed{" "}
                  <code className="text-[13px] text-blue-2">hlx_sk_</code>) run
                  the complete agent — system prompt, tools, skills, and RAG,
                  including on-chain actions. Keys are shown once, stored only as
                  a hash, capped per account, and revocable instantly. Usage
                  bills the key owner's Helix Token balance.
                </Li>
                <Li>
                  <strong>x402</strong> lets callers skip a key and pay{" "}
                  <strong>$0.05 USDC on Base</strong> per completion. Unpaid
                  requests return HTTP 402. Guest calls are read-only (prices,
                  wallet reads, swap quotes, protocol docs) and are not billed
                  in HT.
                </Li>
                <Li>
                  <strong>Chat completions</strong> support streaming and
                  non-streaming. API requests are stateless and are not saved to
                  chat history. If a valid key is sent, HT billing wins and x402
                  is ignored.
                </Li>
              </Ul>

              <H3>10.4 x402 Agent Payments</H3>
              <P>
                The agent can fetch any URL. If the endpoint responds with HTTP
                402 Payment Required, Helix pays in USDC (Base by default, or
                whatever network the endpoint requires) and bills the user's paid
                Helix Token balance for the USD spent times the protocol margin.
                Each payment is capped per request. This is separate from paying
                Helix's own API with x402: outbound payments buy third-party
                resources; inbound payments let developers call Helix without a
                key.
              </P>
            </Section>

            <Divider />

            {/* 11. Governance */}
            <Section id="governance" title="11. Governance">
              <P>
                Helix AI is designed to evolve through community input. HLX
                token holders play a central role in shaping the platform's
                future through governance participation.
              </P>

              <H3>11.1 Governance Scope</H3>
              <P>Governance decisions may include:</P>
              <Ul>
                <Li>
                  New protocol and dApp integrations on Ethereum
                </Li>
                <Li>New AI agent capabilities and tool additions</Li>
                <Li>Helix Token pricing and margin adjustments</Li>
                <Li>Treasury allocation for ecosystem grants</Li>
                <Li>Partnership and integration priorities</Li>
                <Li>Platform feature development roadmap</Li>
              </Ul>

              <H3>11.2 Progressive Decentralization</H3>
              <P>
                Helix AI follows a progressive decentralization model. In the
                early phases, the core team retains operational control to ensure
                rapid iteration and platform stability. Over time, governance
                authority is progressively transferred to the community through
                on-chain governance mechanisms, with the goal of achieving a
                fully community-governed protocol.
              </P>
            </Section>

            <Divider />

            {/* 12. Roadmap */}
            <Section id="roadmap" title="12. Roadmap">
              <div className="space-y-8 my-6">
                <RoadmapPhase
                  phase="Phase 1 — Foundation (Completed)"
                  items={[
                    "Core AI agent with natural language blockchain interaction",
                    "Ethereum portfolio tracking",
                    "Token swaps via DEX aggregation (WowMax)",
                    "ERC-20 token deployment with OpenZeppelin support",
                    "Custom Solidity contract deployment and verification",
                    "Usage-metered Helix Token system with daily free allowance",
                    "Non-custodial wallet integration via Thirdweb",
                    "Gas sponsorship on Ethereum via account abstraction",
                  ]}
                />
                <RoadmapPhase
                  phase="Phase 2 — Expansion (Completed)"
                  items={[
                    "Multi-model access to 300+ AI models via a unified router",
                    "User-authored Skills to extend the agent with custom knowledge",
                    "OpenAI-compatible Developer API with revocable API keys",
                    "x402 pay-to-use on the public API (USDC on Base, no key)",
                    "On-chain staking, rewards, and governance",
                    "Executable on-chain proposals (create, vote, queue, execute)",
                    "Autonomous x402 agent payments to paywalled endpoints",
                  ]}
                />
                <RoadmapPhase
                  phase="Phase 3 — Autonomy (In Progress)"
                  items={[
                    "Automated portfolio strategies and yield optimization",
                    "Scheduled and conditional transaction execution",
                    "Multi-agent workflows for complex DeFi strategies",
                    "Advanced smart contract templates (NFTs, DAOs, vesting)",
                    "Analytics dashboard and richer transaction history",
                    "Expanded skill and tool marketplace for the community",
                  ]}
                />
                <RoadmapPhase
                  phase="Phase 4 — Ecosystem"
                  items={[
                    "Fully decentralized governance via HLX token",
                    "Cross-platform agent (Telegram, Discord, browser extension)",
                    "Institutional and enterprise features",
                    "AI-powered security auditing for deployed contracts",
                    "Ecosystem grants program for third-party developers",
                    "Deeper integrations with leading Ethereum DeFi protocols",
                  ]}
                />
              </div>
            </Section>

            <Divider />

            {/* 13. Risk Factors */}
            <Section id="risks" title="13. Risk Factors">
              <P>
                Users and participants should be aware of the following risks
                associated with the Helix AI platform and the HLX token:
              </P>
              <H3>13.1 Smart Contract Risk</H3>
              <P>
                Smart contracts generated and deployed through Helix AI are not
                audited. AI-generated code may contain bugs, vulnerabilities, or
                unintended behavior. Users should independently audit any
                contract before deploying to production or managing significant
                value.
              </P>
              <H3>13.2 AI Limitations</H3>
              <P>
                Large language models can produce incorrect, incomplete, or
                misleading outputs. The AI agent may misinterpret user intent,
                provide inaccurate price data, or construct suboptimal
                transactions. Users should always review transaction details
                before confirming.
              </P>
              <H3>13.3 Market and Token Risk</H3>
              <P>
                The value of HLX tokens and other digital assets is volatile
                and may decrease significantly. HLX tokens are utility tokens
                and should not be considered an investment. There is no
                guarantee of liquidity or future value.
              </P>
              <H3>13.4 Third-Party Risk</H3>
              <P>
                The Services integrate with third-party protocols (WowMax,
                Thirdweb, etc.) that may experience downtime,
                security incidents, or changes in terms that affect the
                Services.
              </P>
              <H3>13.5 Regulatory Risk</H3>
              <P>
                The regulatory landscape for blockchain, AI, and digital assets
                is evolving. Changes in laws or regulations may affect the
                availability or legality of the Services in certain
                jurisdictions.
              </P>
              <H3>13.6 Network Risk</H3>
              <P>
                The Ethereum network may experience congestion, forks, or
                outages that delay or prevent transaction execution. Gas fees
                may fluctuate significantly with network demand.
              </P>
            </Section>

            <Divider />

            {/* 14. Conclusion */}
            <Section id="conclusion" title="14. Conclusion">
              <P>
                Helix AI represents a new paradigm in blockchain interaction —
                one where the complexity of decentralized systems is abstracted
                behind a conversational interface, and users can accomplish in a
                single sentence what previously required navigating multiple
                protocols, interfaces, and networks.
              </P>
              <P>
                By combining access to 300+ frontier AI models with a
                comprehensive blockchain tool suite, user-authored skills, an
                open developer API, non-custodial wallet architecture, and a
                sustainable usage-metered Helix Token system, Helix AI makes
                blockchain accessible to everyone — from first-time users to
                experienced developers.
              </P>
              <P>
                The platform is live at{" "}
                <a
                  href="https://ai.helix.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-2 hover:underline font-medium"
                >
                  ai.helix.org
                </a>
                . We invite you to try it, join the community, and help shape
                the future of autonomous blockchain intelligence.
              </P>
            </Section>
          </main>
        </div>
      </ComponentWrapper>
    </div>
  );
};

export default WhitepaperPage;

/* ── Sub-components ── */

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-[100px] mb-4">
      <h2 className="text-[24px] sm:text-[28px] font-semibold text-blue-1 mb-5 leading-tight">
        {title}
      </h2>
      {children}
    </section>
  );
}

function H3({ children }) {
  return (
    <h3 className="text-[18px] font-semibold text-blue-1 mt-6 mb-2">
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p className="text-[15px] text-gray-600 leading-[26px] mb-3">{children}</p>
  );
}

function Ul({ children }) {
  return (
    <ul className="list-disc list-outside space-y-2 mb-4 pl-5">{children}</ul>
  );
}

function Li({ children }) {
  return (
    <li className="text-[15px] text-gray-600 leading-[24px]">{children}</li>
  );
}

function Divider() {
  return <hr className="border-[#F0F0F5] my-10" />;
}

function StatCard({ label, children }) {
  return (
    <div className="border border-[#F0F0F5] rounded-[16px] p-4">
      <p className="text-[12px] text-gray-1 font-medium mb-1">{label}</p>
      <div className="text-[15px] text-blue-1 font-medium">{children}</div>
    </div>
  );
}

function RoadmapPhase({ phase, items }) {
  return (
    <div className="border-l-[3px] border-blue-2 pl-5">
      <h4 className="text-[17px] font-semibold text-blue-1 mb-3">{phase}</h4>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-blue-2 mt-1 flex-shrink-0 text-[12px]">
              &#9679;
            </span>
            <span className="text-[14px] text-gray-600 leading-[22px]">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
